import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fieldCls = "min-w-0 flex-1 rounded-xl border border-gold/40 bg-white px-3 py-2.5 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/25";

export default function PhotoUploadForm({ albumId, onUploaded }) {
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);

  const reset = () => {
    setFile(null);
    setCaption("");
    setTags("");
    if (fileInput.current) fileInput.current.value = "";
  };

  useEffect(reset, [albumId]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a photo first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("album_id", albumId);
      fd.append("caption", caption);
      fd.append("tags", tags);
      fd.append("file", file);
      await api.post("/photos/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      reset();
      await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={upload} className="mt-5 rounded-2xl border border-dashed border-gold/50 bg-blush/70 p-4" data-testid="photo-upload-form">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <input ref={fileInput} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="photo-file-input" className="hidden" />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          data-testid="photo-choose-button"
          className="flex items-center justify-center gap-2 rounded-xl border border-burgundy/30 bg-white px-4 py-2.5 text-sm font-medium text-wine transition-all hover:border-gold hover:text-golddeep"
        >
          <ImagePlus className="h-4 w-4" />
          {file ? file.name : "Choose photo"}
        </button>
        <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption (optional)" data-testid="photo-caption-input" className={fieldCls} />
        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="Tags: pink, roses, wedding…" data-testid="photo-tags-input" className={fieldCls} />
        <button
          type="submit"
          disabled={uploading || !file}
          data-testid="photo-upload-button"
          className="flex shrink-0 items-center justify-center gap-2 rounded-xl border border-gold bg-gold px-5 py-2.5 text-sm font-semibold text-white transition-all hover:bg-golddeep disabled:opacity-50"
        >
          {uploading && <Loader2 className="h-4 w-4 animate-spin" />}
          Upload
        </button>
      </div>
      <p className="mt-2 text-xs text-plum/50">Tags power the public search — separate them with commas (colours, themes, occasions).</p>
    </form>
  );
}
